import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '@/services/api'; // Or standard axios instance
import axios from 'axios';

interface QueryHistoryItem {
  id: number;
  question: string;
  timestamp: string;
  resultsCount: number;
  success: boolean;
  results?: any;
}

interface AiQueryResponse {
  success: boolean;
  message?: string;
  data?: {
    answer?: string;
    results?: Record<string, any>[];
    sql?: string;
    usage?: {
      promptTokens: number;
      completionTokens: number;
      totalTokens: number;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

interface AiReportState {
  queryHistory: QueryHistoryItem[];
  currentQuery: string;
  currentResults: AiQueryResponse | null;
  isLoading: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  currentPage: number;
  rowsPerPage: number;
  totalRows: number;
  queryProgress: number;
  queryStartTime: number | null;
}

const initialState: AiReportState = {
  queryHistory: [],
  currentQuery: '',
  currentResults: null,
  isLoading: false,
  errorMessage: null,
  successMessage: null,
  currentPage: 1,
  rowsPerPage: 50,
  totalRows: 0,
  queryProgress: 0,
  queryStartTime: null,
};

let currentAbortController: AbortController | null = null;

/**
 * History ids must be unique: `removeFromHistory` filters by id, so two entries
 * sharing one id are deleted together. `Date.now()` is millisecond-granular and
 * collides on rapid submissions, so derive the id from the newest entry instead
 * (the list is newest-first and strictly decreasing, so this stays unique and
 * keeps the reducer pure).
 */
const nextHistoryId = (history: QueryHistoryItem[]): number => (history[0]?.id ?? 0) + 1;

/** Rejection payload: `cancelled` marks a user abort / superseded request. */
interface AiQueryRejection {
  question?: string;
  errorMsg?: string;
  cancelled?: boolean;
}

export const submitAiQuery = createAsyncThunk(
  'aiReport/submitQuery',
  async (question: string, { rejectWithValue }) => {
    if (currentAbortController) {
      currentAbortController.abort();
    }
    // Capture this request's controller locally: the shared ref must only be
    // cleared by the request that still owns it, otherwise a second (or
    // retried) submit lets the first request's `finally` wipe the new
    // controller and `abortAiQuery()` can no longer cancel it.
    const controller = new AbortController();
    currentAbortController = controller;

    try {
      const response = await api.post<AiQueryResponse>(
        '/report/ai-ask',
        { question: question.trim() },
        {
          signal: controller.signal,
          timeout: 180000,
        }
      );
      
      return { question, response: response.data };
    } catch (error: any) {
      if (axios.isCancel(error)) {
        // Flag it instead of reporting a failure: a manual abort (or a request
        // superseded by the next submit) is not an error and must not push a
        // failed history entry or raise "Query was cancelled".
        return rejectWithValue({ question, errorMsg: '', cancelled: true });
      }

      let errorMsg = 'An error occurred while processing your request';

      // The api.ts response interceptor re-rejects handled statuses as a
      // plain `{ ...body, status }` object (no `response`/`request` props),
      // so read status/errorCode/message from either error shape.
      const status: number | undefined =
        typeof error?.status === 'number' ? error.status : error?.response?.status;
      const errorBody: any = error?.response?.data ?? error;
      const errorCode: string | undefined = errorBody?.errorCode;

      if (typeof status === 'number') {
        switch (status) {
          case 429:
            if (errorCode === 'RATE_LIMIT_EXCEEDED') errorMsg = "You've asked too many questions. Please wait a moment";
            else if (errorCode === 'PROVIDER_RATE_LIMIT') errorMsg = 'AI provider rate limit exceeded. Please try again later';
            else errorMsg = 'Too many requests. Please wait a moment';
            break;
          case 408:
            if (errorCode === 'QUERY_TIMEOUT') errorMsg = 'Request timeout. Please try again';
            else if (errorCode === 'PROVIDER_TIMEOUT') errorMsg = 'AI provider request timeout. Please try again';
            else errorMsg = 'Request timeout. Please try again';
            break;
          case 500:
            if (errorCode === 'DATABASE_ERROR') errorMsg = 'Database error occurred. Our team has been notified';
            else if (errorCode === 'PROVIDER_ERROR') errorMsg = 'AI provider error. Please try again';
            else errorMsg = 'Something went wrong. Our team has been notified';
            break;
          default:
            errorMsg = errorBody?.message || errorMsg;
        }
      } else if (error.request) {
        errorMsg = 'Network error. Please check your connection';
      } else {
        errorMsg = error.message || errorMsg;
      }

      return rejectWithValue({ question, errorMsg, cancelled: false });
    } finally {
      // Only clear the shared ref when this request still owns it — an older
      // request finishing must not disarm the newer in-flight controller.
      if (currentAbortController === controller) {
        currentAbortController = null;
      }
    }
  }
);

export const abortAiQuery = () => {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
};

export const aiReportSlice = createSlice({
  name: 'aiReport',
  initialState,
  reducers: {
    setCurrentQuery(state, action: PayloadAction<string>) {
      state.currentQuery = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setRowsPerPage(state, action: PayloadAction<number>) {
      state.rowsPerPage = action.payload;
      state.currentPage = 1;
    },
    clearHistory(state) {
      state.queryHistory = [];
    },
    removeFromHistory(state, action: PayloadAction<number>) {
      state.queryHistory = state.queryHistory.filter(item => item.id !== action.payload);
    },
    revisitQuery(state, action: PayloadAction<QueryHistoryItem>) {
      state.currentQuery = action.payload.question;
      if (action.payload.results) {
        state.currentResults = action.payload.results;
        state.totalRows = action.payload.results.data?.results?.length || 0;
        state.currentPage = 1;
        state.successMessage = 'Loaded from history';
        state.errorMessage = null;
      } else {
        state.errorMessage = 'This query did not have saved results';
      }
    },
    clearCurrentState(state) {
      state.currentQuery = '';
      state.currentResults = null;
      state.errorMessage = null;
      state.successMessage = null;
      state.currentPage = 1;
      state.queryProgress = 0;
      state.queryStartTime = null;
    },
    manualAbortQuery(state) {
      // Reducers stay pure: `abortAiQuery()` is deliberately NOT called here.
      // The abort control (ai-query/index.tsx) aborts the in-flight request
      // itself, and the resulting `submitAiQuery.rejected` with `cancelled:
      // true` performs this same UI reset. This action is a direct-reset
      // fallback and is currently unreferenced.
      state.isLoading = false;
      state.queryProgress = 0;
      state.queryStartTime = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitAiQuery.pending, (state, action) => {
        state.isLoading = true;
        state.errorMessage = null;
        state.successMessage = null;
        state.currentResults = null;
        state.currentQuery = action.meta.arg;
        state.queryStartTime = Date.now();
        state.queryProgress = 0;
      })
      .addCase(submitAiQuery.fulfilled, (state, action) => {
        const { question, response } = action.payload;
        state.isLoading = false;
        state.queryProgress = 0;
        state.queryStartTime = null;

        if (response.success) {
          state.currentResults = response;
          state.totalRows = response.data?.results?.length || 0;
          state.currentPage = 1;
          state.successMessage = response.message || 'Query processed successfully';
          
          state.queryHistory.unshift({
            id: nextHistoryId(state.queryHistory),
            question,
            timestamp: new Date().toISOString(),
            resultsCount: response.data?.results?.length || 0,
            success: true,
            results: response,
          });
        } else {
          state.currentResults = response;
          state.errorMessage = response.message || 'An error occurred while processing your request';
          
          state.queryHistory.unshift({
            id: nextHistoryId(state.queryHistory),
            question,
            timestamp: new Date().toISOString(),
            resultsCount: 0,
            success: false,
          });
        }

        if (state.queryHistory.length > 50) {
          state.queryHistory = state.queryHistory.slice(0, 50);
        }
      })
      .addCase(submitAiQuery.rejected, (state, action) => {
        const payload = action.payload as AiQueryRejection | undefined;
        state.isLoading = false;
        state.queryProgress = 0;
        state.queryStartTime = null;

        if (payload?.cancelled) {
          // Benign: the user aborted or a newer submit superseded this one.
          // No error banner and no failed history entry.
          state.errorMessage = null;
          return;
        }

        state.errorMessage = payload?.errorMsg || 'Error occurred';
        
        if (payload?.question) {
          state.queryHistory.unshift({
            id: nextHistoryId(state.queryHistory),
            question: payload.question,
            timestamp: new Date().toISOString(),
            resultsCount: 0,
            success: false,
          });
          
          if (state.queryHistory.length > 50) {
            state.queryHistory = state.queryHistory.slice(0, 50);
          }
        }
      });
  },
});

export const {
  setCurrentQuery,
  setPage,
  setRowsPerPage,
  clearHistory,
  removeFromHistory,
  revisitQuery,
  clearCurrentState,
  manualAbortQuery,
} = aiReportSlice.actions;

export default aiReportSlice.reducer;

export const loadQueryHistory = () => (_dispatch: any) => {};


export const setQuery = (q: string) => setCurrentQuery(q);
export const setError = (_e: string) => (_dispatch: any) => {};
export const setSuccess = (_s: string) => (_dispatch: any) => {};


export const submitQuery = submitAiQuery;
export const abortQuery = abortAiQuery;

